/**
 * NEXUS UI - Waveform
 * Real-time oscilloscope-style waveform display
 */

import React, { useRef, useEffect, useMemo } from 'react';
import type { BaseProps, NexusVariant } from './types';

export interface WaveformProps extends BaseProps {
  data: number[];
  variant?: NexusVariant;
  width?: number;
  height?: number;
  showGrid?: boolean;
  showZeroLine?: boolean;
  strokeWidth?: number;
  fill?: boolean;
  smoothing?: number; // 0-1, higher = smoother
  minValue?: number;
  maxValue?: number;
}

const variantColors: Record<NexusVariant, { base: string; glow: string; fill: string }> = {
  cyan: { base: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)', fill: 'rgba(0, 212, 255, 0.1)' },
  blue: { base: '#3b82f6', glow: 'rgba(59, 130, 246, 0.6)', fill: 'rgba(59, 130, 246, 0.1)' },
  violet: { base: '#a855f7', glow: 'rgba(168, 85, 247, 0.6)', fill: 'rgba(168, 85, 247, 0.1)' },
  green: { base: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)', fill: 'rgba(0, 255, 136, 0.1)' },
  amber: { base: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)', fill: 'rgba(255, 170, 0, 0.1)' },
  red: { base: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)', fill: 'rgba(255, 51, 102, 0.1)' },
};

export const Waveform: React.FC<WaveformProps> = ({
  data,
  variant = 'cyan',
  width = 400,
  height = 120,
  showGrid = true,
  showZeroLine = true,
  strokeWidth = 2,
  fill = true,
  smoothing = 0.2,
  minValue,
  maxValue,
  className = '',
  style,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const colors = variantColors[variant];

  const { min, max, centerY } = useMemo(() => {
    const dataMin = minValue ?? Math.min(...data, 0);
    const dataMax = maxValue ?? Math.max(...data, 0);
    const center = height / 2;
    return { min: dataMin, max: dataMax, centerY: center };
  }, [data, height, minValue, maxValue]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set up for high DPI displays
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    // Clear
    ctx.clearRect(0, 0, width, height);

    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = 'rgba(0, 212, 255, 0.08)';
      ctx.lineWidth = 1;

      // Vertical lines
      const vLines = 10;
      for (let i = 0; i <= vLines; i++) {
        const x = (i / vLines) * width;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }

      // Horizontal lines
      const hLines = 4;
      for (let i = 0; i <= hLines; i++) {
        const y = (i / hLines) * height;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
    }

    // Draw zero line
    if (showZeroLine) {
      ctx.strokeStyle = 'rgba(0, 212, 255, 0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(0, centerY);
      ctx.lineTo(width, centerY);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    if (data.length < 2) return;

    // Calculate points
    const range = max - min || 1;
    const points = data.map((value, index) => ({
      x: (index / (data.length - 1)) * width,
      y: height - ((value - min) / range) * height,
    }));

    // Apply smoothing (cardinal spline)
    const getControlPoints = (p0: { x: number; y: number }, p1: { x: number; y: number }, p2: { x: number; y: number }, t: number) => {
      const d01 = Math.sqrt(Math.pow(p1.x - p0.x, 2) + Math.pow(p1.y - p0.y, 2));
      const d12 = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
      const fa = t * d01 / (d01 + d12);
      const fb = t * d12 / (d01 + d12);

      return {
        cp1: { x: p1.x - fa * (p2.x - p0.x), y: p1.y - fa * (p2.y - p0.y) },
        cp2: { x: p1.x + fb * (p2.x - p0.x), y: p1.y + fb * (p2.y - p0.y) },
      };
    };

    // Draw fill
    if (fill) {
      ctx.beginPath();
      ctx.moveTo(points[0].x, height);
      ctx.lineTo(points[0].x, points[0].y);

      for (let i = 1; i < points.length; i++) {
        if (smoothing > 0 && i < points.length - 1) {
          const p0 = points[Math.max(0, i - 2)];
          const p1 = points[i - 1];
          const p2 = points[i];
          const p3 = points[Math.min(points.length - 1, i + 1)];

          const { cp2: cp1 } = getControlPoints(p0, p1, p2, smoothing);
          const { cp1: cp2 } = getControlPoints(p1, p2, p3, smoothing);

          ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, p2.x, p2.y);
        } else {
          ctx.lineTo(points[i].x, points[i].y);
        }
      }

      ctx.lineTo(points[points.length - 1].x, height);
      ctx.closePath();

      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, colors.fill);
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fill();
    }

    // Draw glow line
    ctx.shadowColor = colors.glow;
    ctx.shadowBlur = 8;
    ctx.strokeStyle = colors.base;
    ctx.lineWidth = strokeWidth + 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length; i++) {
      if (smoothing > 0 && i < points.length - 1) {
        const p0 = points[Math.max(0, i - 2)];
        const p1 = points[i - 1];
        const p2 = points[i];
        const p3 = points[Math.min(points.length - 1, i + 1)];

        const { cp2: cp1 } = getControlPoints(p0, p1, p2, smoothing);
        const { cp1: cp2 } = getControlPoints(p1, p2, p3, smoothing);

        ctx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, p2.x, p2.y);
      } else {
        ctx.lineTo(points[i].x, points[i].y);
      }
    }

    ctx.stroke();

    // Draw main line
    ctx.shadowBlur = 0;
    ctx.lineWidth = strokeWidth;
    ctx.stroke();

    // Draw end point glow
    const lastPoint = points[points.length - 1];
    ctx.beginPath();
    ctx.arc(lastPoint.x, lastPoint.y, 4, 0, Math.PI * 2);
    ctx.fillStyle = colors.base;
    ctx.shadowColor = colors.glow;
    ctx.shadowBlur = 12;
    ctx.fill();

  }, [data, width, height, colors, showGrid, showZeroLine, strokeWidth, fill, smoothing, min, max, centerY]);

  return (
    <div
      className={`nx-waveform ${className}`}
      style={{
        position: 'relative',
        background: 'var(--nx-void-panel)',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        borderRadius: '4px',
        overflow: 'hidden',
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width,
          height,
          display: 'block',
        }}
      />

      {/* Scanline effect removed - was too visually noisy for production use */}
    </div>
  );
};

export default Waveform;
